import { BADGE_CATEGORY, BadgeDefinition, BadgeLadderTier } from "@shared/models";

// Relative, not the "@containers" alias: jest resolves no path aliases in this repo, and this module's
// test needs the REAL resolvers (their PNGs resolve to their own basenames under jest).
import { resolveBadgeIcon } from "../../Badges/badgeArt";
import { FLINT_EMBLEM_ART } from "../../Badges/flameBadgeArt";

/**
 * The Lifetime Flame card's emblem image: the image of the rung the viewer stands on, or -- not started
 * (`current_tier: null`) -- the dedicated Flint emblem (Figma board icon 1, the grey crystal), never the
 * ladder's first rung. Flint isn't a catalog entry, so the not-started case is answered without touching
 * `badgeDefinitions` at all -- it resolves the same way whether the catalog has loaded, failed, or is
 * still `null`. The current-tier path is unchanged: it goes through the same `resolveBadgeIcon` rule the
 * Badges screen's tiles use (served `icon_url` first, then bundled art), so the card and the grid can
 * never disagree about what a tier looks like. No core values are passed: flame codes never need them.
 *
 * The flame progress response carries only `index`/`name`/`threshold`, no badge `code`, so the rung is
 * found in the catalog by the api's OWN rule for picking `current_tier` (`badge.service.ts`
 * `findTierByIndex`: `tier === index + 1` among the flame definitions) -- not by name, and not by the
 * catalog array's position, neither of which the api guarantees.
 *
 * @param currentTier - the flame progress response's own `current_tier`; `null` when not started.
 * @param badgeDefinitions - the badge catalog, or `null` while it hasn't loaded / failed to load.
 * @returns the URL to draw. Always non-null when not started (the Flint emblem). When a tier is set,
 *   `null` when the catalog isn't available or the rung has no image -- the caller keeps its own
 *   fallback emblem for that case.
 */
export const flameEmblemArt = (
  currentTier: BadgeLadderTier | null,
  badgeDefinitions: BadgeDefinition[] | null,
): string | null => {
  if (!currentTier) return FLINT_EMBLEM_ART;
  if (!badgeDefinitions) return null;

  const wantedTier = currentTier.index + 1;
  const definition = badgeDefinitions.find(
    (candidate) => candidate.category === BADGE_CATEGORY.FLAME && candidate.tier === wantedTier,
  );

  return definition ? resolveBadgeIcon(definition.icon_url, definition.code, []) : null;
};
